import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Home, Shield, DollarSign, List, ArrowRight, CheckCircle2, Calculator } from 'lucide-react';
import { SimonsHeader, SimonsFooter } from '../components/SimonsNavigation';
export function SimonsLandlord({
  onBack,
  setActivePage
}: {
  onBack: () => void;
  setActivePage: (p: any) => void;
}) {
  const [units, setUnits] = useState(5);
  return <div className="min-h-screen bg-[#F8FAFC] text-[#002366] font-sans" data-id="element-1783">
      <SimonsHeader activePage="landlord" setActivePage={setActivePage} data-id="element-1784" />

      {/* Hero / Calculator */}
      <section className="py-24 px-8 max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-20 items-center" data-id="element-1785">
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} data-id="element-1786">
          <h1 className="text-5xl lg:text-6xl font-black mb-8 leading-tight tracking-tight" data-id="element-1787">
            Portfolio <br data-id="element-1788" />
            <span className="text-[#D32F2F]" data-id="element-1789">Management.</span>
          </h1>
          <p className="text-xl text-slate-500 mb-10 font-medium leading-relaxed" data-id="element-1790">
            Simplified insurance for real estate investors. Protect your cash
            flow and assets with a single, unified portfolio policy.
          </p>

          <div className="bg-white p-8 rounded-[24px] shadow-xl border border-slate-100" data-id="element-1791">
            <div className="flex items-center gap-3 mb-6" data-id="element-1792">
              <Calculator className="text-[#FFD700]" data-id="element-1793" />
              <h3 className="font-black uppercase tracking-widest text-sm" data-id="element-1794">
                Multi-Property Savings
              </h3>
            </div>
            <div className="space-y-6" data-id="element-1795">
              <div data-id="element-1796">
                <div className="flex justify-between mb-2" data-id="element-1797">
                  <span className="text-xs font-bold text-slate-400 uppercase" data-id="element-1798">
                    Number of Units
                  </span>
                  <span className="text-lg font-black" data-id="element-1799">{units}</span>
                </div>
                <input type="range" min="1" max="50" value={units} onChange={e => setUnits(parseInt(e.target.value))} className="w-full h-2 bg-slate-100 rounded-lg appearance-none cursor-pointer accent-[#D32F2F]" data-id="element-1800" />
              </div>
              <div className="pt-4 border-t border-slate-50 flex justify-between items-center" data-id="element-1801">
                <span className="text-sm font-bold text-slate-500" data-id="element-1802">
                  Estimated Annual Savings
                </span>
                <span className="text-3xl font-black text-[#002366]" data-id="element-1803">
                  ${(units * 145).toLocaleString()}
                </span>
              </div>
            </div>
          </div>
        </motion.div>

        <div className="bg-[#002366] rounded-[32px] p-12 text-white shadow-2xl relative overflow-hidden" data-id="element-1804">
          <h2 className="text-2xl font-black mb-8 flex items-center gap-3" data-id="element-1805">
            <List className="text-[#FFD700]" data-id="element-1806" />
            Simplified Coverage
          </h2>
          <div className="space-y-4" data-id="element-1807">
            {[{
            title: 'Renters Liability',
            desc: 'Protection against tenant-related incidents.'
          }, {
            title: 'Structure Protection',
            desc: 'Full replacement cost for your physical assets.'
          }, {
            title: 'Loss of Income',
            desc: 'Cash flow security during property repairs.'
          }, {
            title: 'Legal Defense',
            desc: 'Expert representation for landlord-tenant disputes.'
          }].map((item, i) => <div key={i} className="flex items-start gap-4 p-4 bg-white/5 rounded-xl border border-white/10" data-id="element-1808">
                <CheckCircle2 className="text-[#FFD700] shrink-0 mt-1" size={18} data-id="element-1809" />
                <div data-id="element-1810">
                  <p className="font-bold text-sm" data-id="element-1811">{item.title}</p>
                  <p className="text-xs text-white/40" data-id="element-1812">{item.desc}</p>
                </div>
              </div>)}
          </div>
          <div className="absolute -bottom-10 -right-10 w-40 h-40 bg-[#D32F2F]/20 blur-3xl rounded-full" data-id="element-1813"></div>
        </div>
      </section>

      {/* Portfolio Review Form */}
      <section className="py-24 px-8 bg-white border-y border-slate-200" data-id="element-1814">
        <div className="max-w-3xl mx-auto text-center" data-id="element-1815">
          <h2 className="text-3xl font-black mb-6" data-id="element-1816">Request Portfolio Review</h2>
          <p className="text-slate-500 font-medium mb-12" data-id="element-1817">
            Send us your current schedule of real estate and we'll provide a
            strategic consolidation quote.
          </p>
          <form className="grid grid-cols-1 md:grid-cols-2 gap-4 text-left" data-id="element-1818">
            <div className="space-y-2" data-id="element-1819">
              <label className="text-[10px] font-black uppercase text-slate-400" data-id="element-1820">
                Full Name
              </label>
              <input type="text" className="w-full bg-slate-50 border border-slate-200 rounded-xl px-4 py-3 focus:outline-none focus:border-[#002366]" placeholder="Investor Name" data-id="element-1821" />
            </div>
            <div className="space-y-2" data-id="element-1822">
              <label className="text-[10px] font-black uppercase text-slate-400" data-id="element-1823">
                Portfolio Size
              </label>
              <select className="w-full bg-slate-50 border border-slate-200 rounded-xl px-4 py-3 focus:outline-none focus:border-[#002366]" data-id="element-1824">
                <option data-id="element-1825">1-5 Properties</option>
                <option data-id="element-1826">5-20 Properties</option>
                <option data-id="element-1827">20+ Properties</option>
              </select>
            </div>
            <button className="md:col-span-2 mt-4 py-4 bg-[#002366] text-white rounded-xl font-black uppercase tracking-widest hover:bg-[#001a4d] transition-all shadow-xl shadow-[#002366]/20" data-id="element-1828">
              Start Review
            </button>
          </form>
        </div>
      </section>

      <SimonsFooter data-id="element-1829" />
    </div>;
}